/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Marin GET /channels/access — fetch channels access ({@link CHANNELS_ACCESS_REQUESTED}).
 *
 * @file ChannelsAccessService.js
 */

import { BaseService } from '@greenville/framework';
import { CHANNELS_ACCESS_REQUESTED } from '../../../common/constants';

export default class ChannelsAccessService extends BaseService {
  constructor() {
    super(CHANNELS_ACCESS_REQUESTED, 'ChannelsAccessStatus');
  }

  /** GET /channels/access */
  handleEvent(eventName, eventData = {}) {
    const { bookId } = eventData;

    return this.getClient('marin').get(`channels/access?bookId=${bookId}`);
  }

  /**
   * @typedef {Object} ChannelsAccessChannelAction
   * @property {string} type
   * @property {string} number
   * @property {string} text
   *
   * @typedef {Object} ChannelsAccessChannelTutor
   * @property {string} name
   * @property {string} image
   *
   * @typedef {Object} ChannelsAccessChannel
   * @property {string} title
   * @property {string} url
   * @property {string} description
   * @property {string} courseId
   * @property {string} thumbnail
   * @property {ChannelsAccessChannelTutor} tutor
   * @property {ChannelsAccessChannelAction[]} actions
   * @property {string} group
   * @property {boolean} autoGeneratedCourse
   * @property {string} learnUrl
   * @property {string} exploreUrl
   *
   * @typedef {Object} ChannelsAccessResponse
   * @property {ChannelsAccessChannel} channel
   * @property {'ACTIVE'} status
   * @property {'EXACT'|'EXACT-LITE'|'RELATED'|'GENERIC'} matchType
   *
   * @param {{ data: ChannelsAccessResponse }} response
   * @param {{ bookId?: string, callback?: function(Object): void }} eventData
   */
  /*
   * {
   *   channel: {
   *     title: 'General Biology',
   *     url: 'https://www.studychannel-dev.pearsondev.tech/channels/biology',
   *     description: 'Struggling with evolution, ecosystems, or the human body? ...',
   *     courseId: 'biology',
   *     thumbnail: 'https://static.studychannel-dev.pearsondev.tech/components/grid/images/biology.jpg',
   *     tutor: {
   *       name: 'Jason',
   *       image: 'https://static.studychannel.pearsonprd.tech/courses/biology/thumbnails/0c199075-9f2f-4492-a85b-34c4513068b1'
   *     },
   *     actions: [
   *       { type: 'videos', number: '4400+', text: 'Videos' },
   *       { type: 'questions', number: '28700+', text: 'Questions' }
   *     ],
   *     group: 'Biology',
   *     autoGeneratedCourse: false,
   *     learnUrl: 'https://www.studychannel-dev.pearsondev.tech/channels/biology/learn',
   *     exploreUrl: 'https://www.studychannel-dev.pearsondev.tech/channels/biology/explore'
   *   },
   *   status: 'ACTIVE',
   *   matchType: 'GENERIC'
   * }
   */
  // eslint-disable-next-line class-methods-use-this
  handleResponse(response, eventData = {}) {
    const { data } = response;
    const { callback } = eventData;

    if (typeof callback === 'function') {
      callback({
        ...data,
        bookId: eventData.bookId
      });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  handleError(error, eventData = {}) {
    const { callback, bookId } = eventData;

    if (typeof callback === 'function') {
      callback(null, {
        error,
        bookId
      });
    }
  }
}
